import React from 'react';
import { ShieldCheck, GraduationCap, Globe2, ClipboardCheck, Info, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import { openInquiryPopup } from '../lib/contact';

function NumBadge({ n }) {
  return <div className="absolute -top-3 right-4 w-8 h-8 rounded-full bg-brand text-white text-sm font-bold flex items-center justify-center shadow-lg shadow-brand/40">{n}</div>;
}

const coreRules = [
  'Foreign Medical Graduates (FMGs) must be granted permanent registration strictly in accordance with the NMC regulations.',
  'The primary medical course completed abroad must be of at least 54 months in duration.',
  'A mandatory 12-month internship must be completed at the same foreign medical institution.',
  'The medium of instruction throughout the MBBS programme must be English.',
  'The education & training must be equivalent to an Indian Medical Graduate (IMG).',
  'The university must be registered with the respective regulatory body competent to grant a licence to practice medicine in that country.',
  'A supervised 1-year internship in India after clearing the National Exit Test (NExT) is mandatory before permanent registration in India.',
];

const nonApplicable = [
  'FMGs who acquired a foreign medical degree or primary qualification before the implementation of these regulations.',
  'Candidates who were already pursuing MBBS abroad prior to these regulations coming into force.',
  'Foreign medical graduates specifically exempted by the Commission or the Central Government via notification.',
];

const eligibility = [
  { t: 'Theory, practical & clinical training', d: 'Course structure must be equivalent to Bachelor of Medicine & Bachelor of Surgery (MBBS) of India.' },
  { t: 'Mandatory 1-year internship', d: 'Hands-on training in Community Medicine, General Medicine, Psychiatry, Paediatrics, General Surgery, Anaesthesia, Obstetrics & Gynaecology, Orthopaedics, ENT, Ophthalmology, Dermatology, Emergency/Casualty services, laboratory services and their sub-specialities.' },
  { t: 'Course content', d: 'The course, internship or clerkship must include subjects duly recognised by the Commission; the curriculum must be available for review by appropriate authorities.' },
  { t: 'Same-institution rule', d: 'The entire course, training, internship or clerkship must be completed outside India, at the same foreign medical institution, throughout the course of study.' },
  { t: 'Maximum time limit', d: 'The total duration of the MBBS course abroad must be completed within 10 years from the date of joining the course.' },
];

export default function NmcRegulations() {
  return (
    <div className="bg-white">
      <SEO
        title="NMC Regulations for MBBS Abroad — 2021 Rules Explained | Russian Education"
        description="Complete summary of NMC FMG Regulations 2021 for Indian students pursuing MBBS abroad — 54-month course, 12-month internship, English medium requirements & eligibility for FMGE/NExT."
        path="/nmc-regulations"
        keywords="NMC regulations MBBS abroad, FMG regulations 2021, NMC rules Russia, FMGE eligibility, NExT eligibility Russia"
      />
      {/* Hero */}
      <section className="relative overflow-hidden bg-blue-hero">
        <div className="absolute inset-0 opacity-20"
          style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 200'%3E%3Cpath fill='%23fff' fill-opacity='0.15' d='M100 20c-8 10-4 22 6 26 8 4 20-2 22-12 2-9-4-18-14-18-4 0-9 1-14 4zm0 40c-15 10-30 30-30 55 0 20 15 40 30 40s30-20 30-40c0-25-15-45-30-55z'/%3E%3C/svg%3E\")", backgroundSize: '160px' }} />
        <div className="container mx-auto max-w-7xl px-4 md:px-8 py-14 md:py-20 relative">
          <div className="text-white max-w-4xl">
            <div className="flex items-center gap-4 mb-6">
              <img src="/logo.jpg" alt="Russian Education" className="w-16 h-16 rounded-full border-2 border-white/40 bg-white p-1" />
              <div className="text-xs md:text-sm uppercase tracking-widest text-white/80 font-semibold">your one stop gateway<br />to Russian universities</div>
            </div>
            <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl font-extrabold leading-[1] tracking-tight">
              NMC REGULATIONS<br />FOR MBBS ABROAD
            </h1>
            <p className="mt-6 text-base md:text-lg text-white/90 max-w-3xl leading-relaxed">
              A clear summary of the National Medical Commission (India) regulations that Foreign
              Medical Graduates must follow to be granted permanent registration to practise
              medicine in India.
            </p>
            <Button onClick={openInquiryPopup} data-testid="nmc-talk-btn" className="mt-8 bg-brand hover:bg-brand-dark text-white font-bold h-12 px-8 rounded-full btn-shift">Talk to a Counsellor</Button>
          </div>
        </div>
      </section>

      {/* Intro statement */}
      <section className="py-14 md:py-16">
        <div className="container mx-auto max-w-6xl px-4 md:px-8">
          <h2 className="font-serif text-2xl md:text-4xl lg:text-5xl font-bold text-primary leading-tight max-w-5xl">
            The National Medical Commission has issued new rules that Indian students pursuing MBBS abroad must follow to practise medicine in India
          </h2>
        </div>
      </section>

      {/* Core Regulations */}
      <section className="py-10 md:py-14 bg-muted/50">
        <div className="container mx-auto max-w-6xl px-4 md:px-8">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-xl bg-brand text-white flex items-center justify-center"><ShieldCheck className="w-6 h-6" /></div>
            <h3 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-primary">Key Regulations for FMGs</h3>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {coreRules.map((r, i) => (
              <div key={i} className="relative bg-white rounded-2xl border border-border p-6 shadow-sm card-lift">
                <NumBadge n={i + 1} />
                <p className="text-sm md:text-base text-foreground leading-relaxed font-medium">{r}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Applicability */}
      <section className="py-14 md:py-16">
        <div className="container mx-auto max-w-6xl px-4 md:px-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-primary text-white flex items-center justify-center"><Info className="w-6 h-6" /></div>
            <h3 className="font-serif text-3xl md:text-4xl font-bold text-primary">Applicability of the Regulations</h3>
          </div>

          <div className="bg-primary text-white rounded-2xl p-7 md:p-9 shadow-lg">
            <p className="text-base md:text-lg font-semibold">These regulations will <span className="text-white bg-brand px-2 py-0.5 rounded">not</span> be applicable to:</p>
            <ul className="mt-5 space-y-3">
              {nonApplicable.map((n, i) => (
                <li key={i} className="flex items-start gap-3">
                  <div className="w-7 h-7 rounded-full bg-white/20 border border-white/40 flex items-center justify-center shrink-0 mt-0.5 font-bold text-sm">{String.fromCharCode(97 + i)}</div>
                  <p className="text-sm md:text-base text-white/95 leading-relaxed">{n}</p>
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-6 grid md:grid-cols-2 gap-4">
            <div className="bg-white rounded-2xl border border-border p-6 shadow-sm">
              <div className="text-xs uppercase tracking-widest text-brand font-bold">Governed by</div>
              <p className="mt-2 text-sm md:text-base text-foreground leading-relaxed">
                FMGs who acquired an MBBS degree from abroad, or candidates already pursuing their
                MBBS in a foreign medical university before the implementation of these regulations,
                shall continue to be governed by the previously applicable regulations.
              </p>
            </div>
            <div className="bg-white rounded-2xl border border-border p-6 shadow-sm">
              <div className="text-xs uppercase tracking-widest text-brand font-bold">Reserved Rights</div>
              <p className="mt-2 text-sm md:text-base text-foreground leading-relaxed">
                The Commission reserves the right to refuse permanent registration to any candidate
                whose actions are found to breach public policy or national security.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Recognition Criteria */}
      <section className="py-14 md:py-16 bg-muted/50">
        <div className="container mx-auto max-w-6xl px-4 md:px-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-brand text-white flex items-center justify-center"><ClipboardCheck className="w-6 h-6" /></div>
            <h3 className="font-serif text-3xl md:text-4xl font-bold text-primary">Recognition Criteria for FMGs</h3>
          </div>
          <p className="text-muted-foreground text-base md:text-lg max-w-3xl mb-8">
            To practise medicine in India, Foreign Medical Graduates must satisfy education and training standards equivalent to those of an Indian Medical Graduate.
          </p>

          <div className="bg-white rounded-2xl border border-border p-7 md:p-9 shadow-sm">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center"><GraduationCap className="w-5 h-5" /></div>
              <h4 className="font-serif text-xl md:text-2xl font-bold text-primary">Eligibility &amp; Duration Criteria</h4>
            </div>
            <div className="grid md:grid-cols-2 gap-5">
              {eligibility.map((e, i) => (
                <div key={i} className="flex items-start gap-3 p-4 rounded-xl bg-muted border border-border">
                  <div className="w-7 h-7 rounded-full bg-brand text-white flex items-center justify-center shrink-0 mt-0.5">
                    <Check className="w-4 h-4" strokeWidth={3} />
                  </div>
                  <div>
                    <div className="font-serif font-bold text-primary text-base leading-tight">{e.t}</div>
                    <p className="mt-1 text-sm text-muted-foreground leading-relaxed">{e.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Advantage Card */}
      <section className="py-14 md:py-16">
        <div className="container mx-auto max-w-6xl px-4 md:px-8">
          <div className="bg-primary text-white rounded-2xl p-8 md:p-10 relative overflow-hidden">
            <div className="absolute inset-0 opacity-10"
              style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'%3E%3Cpath fill='%23fff' d='M50 15c-6 6-4 14 3 17 6 3 15-2 15-10 0-6-6-12-13-12-2 0-4 2-5 5zm0 30c-14 8-26 25-26 42 0 12 12 24 26 24s26-12 26-24c0-17-12-34-26-42z'/%3E%3C/svg%3E\")", backgroundSize: '120px' }} />
            <div className="relative grid md:grid-cols-[1fr_auto] gap-6 items-center">
              <div>
                <div className="text-xs uppercase tracking-widest text-brand font-bold mb-2">Russian Education Advantage</div>
                <h3 className="font-serif text-2xl md:text-3xl font-bold leading-tight">
                  Our 64 partner universities in Russia are fully NMC-compliant — 6-year English-medium MBBS with in-house 12-month internship and complete same-institution training.
                </h3>
              </div>
              <Button className="bg-brand hover:bg-brand-dark text-white font-bold h-12 px-7 rounded-full btn-shift shrink-0">
                See NMC-Approved Universities
              </Button>
            </div>
          </div>
        </div>
      </section>

      <div className="py-4 bg-white border-t border-border">
        <div className="container mx-auto max-w-6xl px-4 md:px-8 flex flex-col md:flex-row items-center justify-between gap-2 text-xs text-muted-foreground">
          <div>© 1988 — 2026, Russian Education</div>
          <div>All rights reserved</div>
        </div>
      </div>
    </div>
  );
}
